"use client";

import {
  AdminRoleIcon,
  MemberRoleIcon,
  SuperuserRoleIcon,
  SystemUserRoleIcon,
} from "@/components/ui/icons/role-icons";
import {
  STATUS_TONE_CLASSES,
  statusTone,
  type StatusTone,
} from "@/lib/constants/status-tone";
import { cn } from "@/lib/utils/general-utils";
import type { ComponentType, SVGProps } from "react";

const badgeColorClasses = {
  red: "bg-red-500/15 text-red-700",
  orange: "bg-orange-500/15 text-orange-800",
  amber: "bg-amber-400/20 text-amber-800",
  yellow: "bg-yellow-400/20 text-yellow-800",
  lime: "bg-lime-400/20 text-lime-800",
  green: "bg-green-500/15 text-green-800",
  emerald: "bg-emerald-500/15 text-emerald-800",
  teal: "bg-teal-500/15 text-teal-800",
  cyan: "bg-cyan-400/20 text-cyan-800",
  sky: "bg-sky-500/15 text-sky-800",
  blue: "bg-blue-500/15 text-blue-700",
  indigo: "bg-indigo-500/15 text-indigo-700",
  violet: "bg-violet-500/15 text-violet-700",
  purple: "bg-purple-500/15 text-purple-700",
  fuchsia: "bg-fuchsia-400/15 text-fuchsia-700",
  pink: "bg-pink-400/15 text-pink-700",
  rose: "bg-rose-400/15 text-rose-700",
  zinc: "bg-gray-600/10 text-gray-700",
} as const;

export type BadgeColor = keyof typeof badgeColorClasses;
export type BadgeSize = "sm" | "md" | "lg";
export type BadgeShape = "pill" | "badge";
export type BadgeVariant =
  | "default"
  | "status"
  | "role"
  | "professional-status"
  | "professional-neutral";

const variantDefaultShape: Record<BadgeVariant, BadgeShape> = {
  default: "badge",
  status: "badge",
  role: "pill",
  "professional-status": "badge",
  "professional-neutral": "badge",
};

const shapeClasses: Record<BadgeShape, string> = {
  pill: "rounded-full",
  badge: "rounded-md",
};

/** Badge colour per status tone (see `lib/constants/status-tone.ts`). */
export const STATUS_TONE_BADGE_COLOR: Record<StatusTone, BadgeColor> = {
  success: "green",
  info: "blue",
  scheduled: "purple",
  warning: "amber",
  error: "red",
  neutral: "zinc",
};

/** Badge colour for a status string, via the shared status-tone map. */
export function getStatusBadgeColor(status: string): BadgeColor {
  return STATUS_TONE_BADGE_COLOR[statusTone(status)];
}

export function getRoleBadgeColor(role: string): BadgeColor {
  switch (role?.toLowerCase()) {
    case "superuser":
      return "rose";
    case "admin":
      return "violet";
    case "system user":
      return "teal";
    case "member":
    case "user":
      return "sky";
    default:
      return "zinc";
  }
}

export function getRoleIcon(
  role: string,
): ComponentType<SVGProps<SVGSVGElement>> | null {
  switch (role?.toLowerCase()) {
    case "superuser":
      return SuperuserRoleIcon;
    case "admin":
      return AdminRoleIcon;
    case "system user":
      return SystemUserRoleIcon;
    case "member":
    case "user":
      return MemberRoleIcon;
    default:
      return null;
  }
}

export interface BadgeProps extends Omit<
  React.ComponentPropsWithoutRef<"span">,
  "className"
> {
  children: React.ReactNode;
  className?: string;
  variant?: BadgeVariant;
  /** Override the default shape. Categorical variants default to "pill", status variants to "badge". */
  shape?: BadgeShape;
  status?: string;
  role?: string;
  color?: BadgeColor;
  size?: BadgeSize;
}

const sizeClasses: Record<BadgeSize, string> = {
  sm: "text-xs",
  md: "text-sm/5 sm:text-xs/5",
  lg: "text-base",
};

export function Badge({
  children,
  className,
  variant = "default",
  shape,
  status,
  role,
  color,
  size = "md",
  ...props
}: BadgeProps) {
  const resolvedShape = shape ?? variantDefaultShape[variant];
  if (variant === "professional-status" && status) {
    const colors = {
      bg: "bg-gray-50",
      text: "text-gray-700",
      border: STATUS_TONE_CLASSES[statusTone(status)].accent,
    };

    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 whitespace-nowrap",
          "text-xs font-medium border-l-2",
          colors.bg,
          colors.text,
          colors.border,
          className,
        )}
        {...props}
      >
        {children}
      </span>
    );
  }

  if (variant === "professional-neutral") {
    return (
      <span
        className={cn(
          "inline-flex items-center rounded-md px-2 py-0.5 whitespace-nowrap",
          "text-xs font-medium bg-gray-100 text-gray-700",
          className,
        )}
        {...props}
      >
        {children}
      </span>
    );
  }

  const finalColor =
    color ||
    (() => {
      switch (variant) {
        case "status":
          return status ? getStatusBadgeColor(status) : "zinc";
        case "role":
          return role ? getRoleBadgeColor(role) : "zinc";
        default:
          return "zinc";
      }
    })();

  const RoleIcon = variant === "role" && role ? getRoleIcon(role) : null;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-x-1.5 px-1.5 py-0.5 font-medium whitespace-nowrap forced-colors:outline",
        shapeClasses[resolvedShape],
        sizeClasses[size],
        badgeColorClasses[finalColor],
        className,
      )}
      {...props}
    >
      {RoleIcon && <RoleIcon className="h-3.5 w-3.5 shrink-0" />}
      {children}
    </span>
  );
}
